import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getCurrency } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const blank = { name: "", price: 0.75, stock: 50, low_stock_threshold: 20, available: true };

export default function Inventory() {
  const [toppings, setToppings] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/toppings").then((r) => setToppings(r.data));
  useEffect(() => { load(); }, []);

  const updateField = async (id, patch) => {
    await api.patch(`/toppings/${id}`, patch);
    load();
  };

  const save = async () => {
    if (!editing.name) return toast.error("Name required");
    if (editing.id) {
      const { id, ...payload } = editing;
      await api.patch(`/toppings/${id}`, payload);
    } else {
      await api.post("/toppings", editing);
    }
    setEditing(null);
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this topping?")) return;
    await api.delete(`/toppings/${id}`);
    load();
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-6 bg-white border-b border-boba-stoneLine flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold">Inventory</h1>
          <p className="text-sm text-boba-inkSoft mt-1">Toppings stock & alerts</p>
        </div>
        <button onClick={() => setEditing({ ...blank })} className="h-11 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium flex items-center gap-2" data-testid="add-topping-btn">
          <Plus size={18} weight="bold" /> Add Topping
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6">
        <div className="bg-white border border-boba-stoneLine rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-boba-muted text-xs uppercase tracking-widest text-boba-inkSoft font-mono">
              <tr>
                <th className="text-left px-4 py-3">Topping</th>
                <th className="text-right px-4 py-3">Price</th>
                <th className="text-right px-4 py-3">Stock</th>
                <th className="text-right px-4 py-3">Threshold</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Available</th>
                <th className="text-right px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="toppings-table">
              {toppings.map((t) => {
                const low = t.stock <= t.low_stock_threshold;
                return (
                  <tr key={t.id} className="border-t border-boba-stoneLine">
                    <td className="px-4 py-3 font-semibold">{t.name}</td>
                    <td className="px-4 py-3 text-right font-mono">{getCurrency()}{t.price.toFixed(2)}</td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={t.stock}
                        onChange={(e) => updateField(t.id, { stock: parseInt(e.target.value) || 0 })}
                        className="w-20 h-9 px-2 border border-boba-stoneLine rounded-md text-right font-mono"
                        data-testid={`stock-${t.id}`}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="number"
                        value={t.low_stock_threshold}
                        onChange={(e) => updateField(t.id, { low_stock_threshold: parseInt(e.target.value) || 0 })}
                        className="w-20 h-9 px-2 border border-boba-stoneLine rounded-md text-right font-mono"
                      />
                    </td>
                    <td className="px-4 py-3">
                      {low ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-red-100 text-boba-danger">
                          <Warning size={12} weight="fill" /> Low
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-boba-matcha/10 text-boba-matcha">OK</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <input
                        type="checkbox"
                        checked={t.available}
                        onChange={(e) => updateField(t.id, { available: e.target.checked })}
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(t)} className="text-sm text-boba-matcha mr-2 hover:underline" data-testid={`edit-topping-${t.id}`}>Edit</button>
                      <button onClick={() => del(t.id)} className="p-2 hover:bg-red-50 text-boba-danger rounded inline-flex"><Trash size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md" data-testid="topping-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing?.id ? "Edit Topping" : "New Topping"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Name</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md" data-testid="topping-name" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Price</label>
                  <input type="number" step="0.05" value={editing.price} onChange={(e) => setEditing({ ...editing, price: parseFloat(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="topping-price" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Stock</label>
                  <input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: parseInt(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" data-testid="topping-stock" />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-boba-inkSoft font-mono">Threshold</label>
                  <input type="number" value={editing.low_stock_threshold} onChange={(e) => setEditing({ ...editing, low_stock_threshold: parseInt(e.target.value) || 0 })} className="w-full mt-1 h-10 px-3 border border-boba-stoneLine rounded-md font-mono" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-3 border-t border-boba-stoneLine">
                <button onClick={() => setEditing(null)} className="h-10 px-4 border border-boba-stoneLine rounded-md hover:bg-stone-50">Cancel</button>
                <button onClick={save} className="h-10 px-4 bg-boba-matcha hover:bg-boba-matchaDark text-white rounded-md font-medium" data-testid="topping-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
